import { useCallback, useRef, useState } from 'react';
import { toast } from '@/lib/toast';
import { useQueryClient } from '@tanstack/react-query';
import { useUploadItemImages } from '@/hooks/useUserItems';

/**
 * Drop-to-upload photos directly on an inventory item card/row.
 * Returns handlers + drag state. No-op when `disabled` is true.
 *
 * - Only activates for OS file drags (dataTransfer.types includes 'Files').
 * - Runs the same conversion pipeline as the photo dialogs (HEIC/DNG).
 * - Caps additions so total photos stay <= 10.
 */
export function useDropToUploadPhotos(opts: {
  itemId: string;
  existingCount: number;
  disabled?: boolean;
}) {
  const { itemId, existingCount, disabled } = opts;
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const dragDepth = useRef(0);
  const uploadImages = useUploadItemImages();
  const queryClient = useQueryClient();

  const hasFiles = (e: React.DragEvent) =>
    Array.from(e.dataTransfer.types || []).includes('Files');

  const onDragEnter = useCallback(
    (e: React.DragEvent) => {
      if (disabled || !hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current += 1;
      setIsDragOver(true);
    },
    [disabled]
  );

  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      if (disabled || !hasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    },
    [disabled]
  );

  const onDragLeave = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setIsDragOver(false);
    },
    [disabled]
  );

  const onDrop = useCallback(
    async (e: React.DragEvent) => {
      if (disabled || !hasFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      dragDepth.current = 0;
      setIsDragOver(false);

      // Snapshot files BEFORE any async work — FileList becomes stale otherwise
      const rawFiles = Array.from(e.dataTransfer.files || []);
      if (rawFiles.length === 0) return;

      const remaining = Math.max(0, 10 - existingCount);
      if (remaining === 0) {
        toast.error('Max 10 photos per item');
        return;
      }

      setIsUploading(true);
      try {
        const { processSelectedFiles } = await import('@/lib/photoInputUtils');
        const files = await processSelectedFiles(rawFiles, { maxCount: remaining });
        if (files.length === 0) {
          setIsUploading(false);
          return;
        }
        await uploadImages.mutateAsync({ itemId, files });
        await queryClient.invalidateQueries({ queryKey: ['my-items'] });
        await queryClient.invalidateQueries({ queryKey: ['user-item', itemId] });
        toast.success(`${files.length} photo${files.length > 1 ? 's' : ''} uploaded`);
      } catch (err) {
        console.error('Drop upload error:', err);
        toast.error('Failed to upload photos');
      } finally {
        setIsUploading(false);
      }
    },
    [disabled, existingCount, itemId, uploadImages, queryClient]
  );

  return {
    isDragOver,
    isUploading,
    dropHandlers: {
      onDragEnter,
      onDragOver,
      onDragLeave,
      onDrop,
    },
  };
}
